import { ApiError, addEventGuest, bulkAddEventGuests, listEventGuests } from '@jq4race/api-client';
import { LocalizedError, useTranslation } from '@jq4race/i18n';
import type {
  BulkAddEventGuestsRequest,
  CreateEventGuestRequest,
  EventGuestResponse,
  EventStatus,
} from '@jq4race/shared';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { isGuestListEditable } from '../constants';
import { AddGuestForm } from './AddGuestForm';
import { BulkImportGuestsForm } from './BulkImportGuestsForm';
import { GuestsTable } from './GuestsTable';

const FEEDBACK_TIMEOUT_MS = 5000;

interface GuestListSectionProps {
  eventId: string;
  eventStatus: EventStatus;
}

export function GuestListSection({ eventId, eventStatus }: GuestListSectionProps) {
  const { t } = useTranslation('events');
  const [guests, setGuests] = useState<EventGuestResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const editable = isGuestListEditable(eventStatus);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listEventGuests(eventId);
      setGuests(res.guests);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? translateApiError(e, t, { fallback: t('guestListSection.loadError') })
          : t('guestListSection.loadError'),
      );
    } finally {
      setLoading(false);
    }
  }, [eventId, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!success && !error) return;
    const id = window.setTimeout(() => {
      setSuccess(null);
      setError(null);
    }, FEEDBACK_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [success, error]);

  async function handleAddGuest(values: CreateEventGuestRequest) {
    try {
      await addEventGuest(eventId, values);
      setSuccess(t('guestListSection.addedSuccess', { name: values.name }));
      await reload();
    } catch (e) {
      throw e instanceof ApiError ? new LocalizedError(translateApiError(e, t)) : e;
    }
  }

  async function handleBulkImport(input: BulkAddEventGuestsRequest) {
    try {
      const res = await bulkAddEventGuests(eventId, input);
      setSuccess(t('guestListSection.guestsImported', { count: res.guests.length }));
      await reload();
    } catch (e) {
      throw e instanceof ApiError ? new LocalizedError(translateApiError(e, t)) : e;
    }
  }

  return (
    <div className="space-y-6">
      <FeedbackBanner tone="error" message={error} onDismiss={() => setError(null)} />
      <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />

      {editable ? (
        <div className="space-y-4 rounded-sm border border-jq-sand/70 bg-jq-surface-0 p-4 sm:p-6">
          <AddGuestForm onSubmit={handleAddGuest} />
          <div className="border-t border-jq-sand/70 pt-4">
            <BulkImportGuestsForm onSubmit={handleBulkImport} />
          </div>
        </div>
      ) : (
        <div className="rounded-sm border border-jq-sand/70 bg-jq-surface-2/50 px-4 py-3 text-[13px] text-jq-graphite">
          {t('guestListSection.readonlyNote')}
        </div>
      )}

      <GuestsTable guests={guests} loading={loading} />
    </div>
  );
}

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('events');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
